import { useMemo, useRef } from 'react';
import { useLoaderData, useNavigate } from 'react-router-dom';
import { Flex, Float, IconButton } from '@chakra-ui/react';
import { ColDef } from '@ag-grid-community/core';
import { AgGridReact } from '@ag-grid-community/react';
import { CompanyDto, RoleDto, UserDto } from '../../lib/api';
import { Grid } from '@/components/ui/grid';
import { UserManagementActionRenderer } from './user-management-action-renderer';
import { PlusIcon } from 'lucide-react';
import { capitalize } from '@/utils/capitalize';
import { GridFilterActionBar } from '@/components/ui/grid-filter-bar';
import { EMPTY_GRID_STATE, usePersistedGridState } from '@/hooks/usePersistedGridState';

/** Short column titles for known seeded role names; unknown names use `role.name`. */
const ROLE_HEADER_ABBREVIATIONS: Record<string, string> = {
    Administrator: 'Admin',
    'Skilled Nurse': 'Staff',
    'QA Administrator': 'QA',
};

const PRIMARY_GRID_ROLE_NAMES = ['Administrator', 'Skilled Nurse', 'QA Administrator'] as const;

export function roleGridHeaderName(roleName: string): string {
    return ROLE_HEADER_ABBREVIATIONS[roleName] ?? roleName;
}

export function orderRolesForUserGrid(roles: RoleDto[]): RoleDto[] {
    const primary = PRIMARY_GRID_ROLE_NAMES.map((name) => roles.find((role) => role.name === name)).filter(
        (role): role is RoleDto => role !== undefined,
    );
    const primaryIds = new Set(primary.map((role) => role.id));
    const rest = roles.filter((role) => !primaryIds.has(role.id)).sort((a, b) => a.name.localeCompare(b.name));
    return [...primary, ...rest];
}

type OrganizationUsersLoaderData = {
    users: UserDto[];
    companies: CompanyDto[];
    roles: RoleDto[];
};

export type OrganizationUserGridRow = UserDto & {
    roleBranchDisplay: Record<string, string>;
};

export function buildOrganizationUserGridRows(
    users: UserDto[],
    branchNamesById: Map<string, string>,
    orderedRoles: RoleDto[],
): OrganizationUserGridRow[] {
    return users.map((user) => {
        const branchesByRole = new Map<string, Set<string>>();
        for (const branchRole of user.branches ?? []) {
            const branchName = branchNamesById.get(branchRole.branchId);
            if (!branchName) {
                continue;
            }

            let branchNames = branchesByRole.get(branchRole.roleId);
            if (!branchNames) {
                branchNames = new Set<string>();
                branchesByRole.set(branchRole.roleId, branchNames);
            }
            branchNames.add(branchName);
        }

        const roleBranchDisplay: Record<string, string> = {};
        for (const role of orderedRoles) {
            const branchNames = branchesByRole.get(role.id);
            if (branchNames && branchNames.size > 0) {
                roleBranchDisplay[role.id] = Array.from(branchNames)
                    .sort((a, b) => a.localeCompare(b))
                    .join(', ');
            } else {
                roleBranchDisplay[role.id] = '-';
            }
        }
        return { ...user, roleBranchDisplay };
    });
}

export const OrganizationUserManagementPage = () => {
    const navigate = useNavigate();
    const data = useLoaderData();
    const { users, companies, roles } = data as OrganizationUsersLoaderData;
    const gridRef = useRef<AgGridReact<OrganizationUserGridRow>>(null);
    const { gridState, saveGridState, applyGridState } = usePersistedGridState('organizationUsersGrid', EMPTY_GRID_STATE);

    const branchNamesById = useMemo(() => {
        const map = new Map<string, string>();
        for (const company of companies) {
            for (const branch of company.branches ?? []) {
                map.set(branch.id, branch.name);
            }
        }
        return map;
    }, [companies]);

    const orderedRoles = useMemo(() => orderRolesForUserGrid(roles), [roles]);

    const rowData = useMemo(
        () => buildOrganizationUserGridRows(users, branchNamesById, orderedRoles),
        [users, branchNamesById, orderedRoles],
    );

    const columnDefs = useMemo<ColDef<OrganizationUserGridRow>[]>(() => {
        return [
            {
                width: 60,
                cellRenderer: UserManagementActionRenderer,
                suppressHeaderFilterButton: true,
                resizable: false,
            },
            {
                colId: 'name',
                headerName: 'Name',
                valueGetter: (params) => {
                    if (params.data) {
                        return `${capitalize(params.data.firstName)} ${capitalize(params.data.lastName)}`;
                    }
                },
                minWidth: 150,
                flex: 1,
                filter: 'agTextColumnFilter',
                floatingFilter: true,
            },
            { field: 'email', minWidth: 250, flex: 1, filter: 'agTextColumnFilter', floatingFilter: true },
            ...orderedRoles.map<ColDef<OrganizationUserGridRow>>((role) => ({
                colId: `role:${role.id}`,
                headerName: roleGridHeaderName(role.name),
                minWidth: 170,
                flex: 1,
                filter: 'agTextColumnFilter',
                floatingFilter: true,
                valueGetter: (params) => {
                    if (!params.data) {
                        return '-';
                    }
                    return params.data.roleBranchDisplay[role.id] ?? '-';
                },
                tooltipValueGetter: (params) => {
                    return typeof params.value === 'string' ? params.value : '';
                },
            })),
            {
                field: 'discipline',
                minWidth: 100,
                maxWidth: 100,
                flex: 1,
                filter: 'agTextColumnFilter',
                floatingFilter: true,
                resizable: false,
            },
            { field: 'active', width: 100, filter: 'agTextColumnFilter', floatingFilter: true, resizable: false },
            { field: 'locked', width: 100, filter: 'agTextColumnFilter', floatingFilter: true, resizable: false },
            { field: 'verified', width: 100, filter: 'agTextColumnFilter', floatingFilter: true, resizable: false },
        ];
    }, [orderedRoles]);

    return (
        <Flex flexDirection="column" style={{ width: '100%', height: '100%' }} gap="10px" position="relative">
            <Float zIndex={10} placement="bottom-end" offset="10">
                <IconButton
                    rounded="full"
                    onClick={() => {
                        navigate('/user/create');
                    }}>
                    <PlusIcon />
                </IconButton>
            </Float>
            <GridFilterActionBar gridRef={gridRef} filterModel={gridState.filterModel} colDefs={columnDefs} />
            <Grid
                countLabel="user"
                gridRef={gridRef}
                suppressCellFocus
                enableCellTextSelection
                rowSelection="single"
                suppressRowHoverHighlight
                columnDefs={columnDefs}
                rowData={rowData}
                onGridReady={(event) => applyGridState(event.api)}
                onFilterChanged={(event) => saveGridState(event.api)}
                onSortChanged={(event) => saveGridState(event.api)}
            />
        </Flex>
    );
};
